/**
 * useProductionCalculator Hook
 * React hook for accessing production calculation functionality
 * 
 * @version 2.0.0
 * @description Provides easy access to production calculations in any component
 */

import { useState, useCallback, useMemo } from 'react';
import ProductionCalculatorService, {
  calculateDailyProduction,
  buildProductionComparison,
  calculateAverages,
  calculateTotals,
  getBblPerInch,
  inchesToBarrels,
  STANDARD_TANK_CONFIGS,
} from '../utils/services/productionCalculatorService';

import type {
  DailyProductionResult,
  ProductionComparison,
  ProductionCalculationInput,
  ProductionCalculationConfig,
  ProductionAverages,
  ProductionTotals,
  TankConfiguration,
} from '../types/production';

// ==================== HOOK INTERFACES ====================

export interface UseProductionCalculatorOptions {
  /** Oil price per BBL for revenue calculations */
  oilPricePerBBL?: number;
  /** Water hauling cost per BBL */
  waterHaulCostPerBBL?: number;
  /** Water disposal cost per BBL */
  waterDisposalCostPerBBL?: number;
  /** Enable Kolor Kut water detection */
  useKolorKut?: boolean;
  /** Auto-calculate on input changes */
  autoCalculate?: boolean;
}

export interface ProductionCalculatorState {
  /** Latest calculation result */
  result: DailyProductionResult | null;
  /** Historical comparison */
  comparison: ProductionComparison | null;
  /** Is currently calculating */
  isCalculating: boolean;
  /** Last error if any */
  error: string | null;
  /** Last calculation timestamp */
  lastCalculated: Date | null;
}

export interface UseProductionCalculatorReturn extends ProductionCalculatorState {
  // Main calculation functions
  calculateProduction: (input: ProductionCalculationInput) => DailyProductionResult;
  calculateWithComparison: (
    input: ProductionCalculationInput,
    historicalResults: DailyProductionResult[]
  ) => ProductionComparison;
  
  // Utility functions
  convertInchesToBarrels: (inches: number, tankSizeBBL: number, customBblPerInch?: number) => number;
  getBblPerInchForTank: (tankSizeBBL: number) => number;
  calculateKolorKut: (
    totalFluidBBL: number,
    waterCutInches: number,
    tankSizeBBL: number
  ) => { netOilBBL: number; waterBBL: number; waterCutPercent: number };
  
  // Period calculations
  calculatePeriodAverages: (results: DailyProductionResult[]) => ProductionAverages;
  calculatePeriodTotals: (results: DailyProductionResult[]) => ProductionTotals;
  
  // Tank utilities
  getTankFillPercent: (volumeBBL: number, capacityBBL: number) => number;
  getTankStatus: (fillPercent: number) => 'empty' | 'low' | 'normal' | 'high' | 'full';
  getStandardTankConfig: (sizeBBL: number) => TankConfiguration | undefined;
  
  // Quick calculations for display
  calculateNetOil: (grossOil: number, kolorKutWater: number) => number;
  calculateWaterCutPercent: (oil: number, water: number) => number;
  calculateGasOilRatio: (gasMCF: number, oilBBL: number) => number;
  calculateDailyRevenue: (oilBBL: number, waterBBL: number) => {
    oilRevenue: number;
    waterCost: number;
    netRevenue: number;
  };
  
  // Reset state
  reset: () => void;
}

// ==================== HOOK IMPLEMENTATION ====================

export function useProductionCalculator(
  options: UseProductionCalculatorOptions = {}
): UseProductionCalculatorReturn {
  const {
    oilPricePerBBL = 75,
    waterHaulCostPerBBL = 2.50,
    waterDisposalCostPerBBL = 1.50,
    useKolorKut = true,
  } = options;

  // State
  const [state, setState] = useState<ProductionCalculatorState>({
    result: null,
    comparison: null,
    isCalculating: false,
    error: null,
    lastCalculated: null,
  });

  // Default config
  const defaultConfig: Partial<ProductionCalculationConfig> = useMemo(() => ({
    oilPricePerBBL,
    waterHaulCostPerBBL,
    waterDisposalCostPerBBL,
    useKolorKut,
    autoLinkHaulTickets: true,
    calculateGasFromMeter: true,
    thresholds: {
      maxWaterCutPercent: 50,
      minDailyOilBBL: 5,
      maxDailyOilBBL: 200,
      oilDropWarningPercent: 20,
      tankFullWarningPercent: 90,
      tankEmptyWarningPercent: 10,
    },
  }), [oilPricePerBBL, waterHaulCostPerBBL, waterDisposalCostPerBBL, useKolorKut]);

  // Main calculation function
  const calculateProduction = useCallback((
    input: ProductionCalculationInput
  ): DailyProductionResult => {
    setState(prev => ({ ...prev, isCalculating: true, error: null }));
    
    try {
      const result = calculateDailyProduction(input, defaultConfig as ProductionCalculationConfig);
      setState(prev => ({
        ...prev,
        result,
        isCalculating: false,
        lastCalculated: new Date(),
      }));
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Calculation failed';
      setState(prev => ({
        ...prev,
        isCalculating: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, [defaultConfig]);

  // Calculate with historical comparison
  const calculateWithComparison = useCallback((
    input: ProductionCalculationInput,
    historicalResults: DailyProductionResult[]
  ): ProductionComparison => {
    setState(prev => ({ ...prev, isCalculating: true, error: null }));
    
    try {
      const result = calculateDailyProduction(input, defaultConfig as ProductionCalculationConfig);
      const comparison = buildProductionComparison(result, historicalResults, defaultConfig as ProductionCalculationConfig);
      
      setState(prev => ({
        ...prev,
        result,
        comparison,
        isCalculating: false,
        lastCalculated: new Date(),
      }));
      
      return comparison;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Calculation failed';
      setState(prev => ({
        ...prev,
        isCalculating: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, [defaultConfig]);

  // Utility: Convert inches to barrels
  const convertInchesToBarrels = useCallback((
    inches: number,
    tankSizeBBL: number,
    customBblPerInch?: number
  ): number => {
    return inchesToBarrels(inches, tankSizeBBL, customBblPerInch);
  }, []);

  // Utility: Get BBL per inch for tank
  const getBblPerInchForTank = useCallback((tankSizeBBL: number): number => {
    return getBblPerInch(tankSizeBBL);
  }, []);

  // Utility: Calculate Kolor Kut adjustment
  const calculateKolorKut = useCallback((
    totalFluidBBL: number,
    waterCutInches: number,
    tankSizeBBL: number
  ) => {
    const bblPerInch = getBblPerInch(tankSizeBBL);
    const waterBBL = waterCutInches * bblPerInch;
    const netOilBBL = Math.max(0, totalFluidBBL - waterBBL);
    const waterCutPercent = totalFluidBBL > 0 ? (waterBBL / totalFluidBBL) * 100 : 0;
    
    return {
      netOilBBL: Math.round(netOilBBL * 100) / 100,
      waterBBL: Math.round(waterBBL * 100) / 100,
      waterCutPercent: Math.round(waterCutPercent * 10) / 10,
    };
  }, []);

  // Period calculations
  const calculatePeriodAverages = useCallback((results: DailyProductionResult[]): ProductionAverages => {
    if (results.length === 0) {
      const today = new Date().toISOString().split('T')[0];
      return calculateAverages([], today, today);
    }
    const sorted = [...results].sort((a, b) => a.date.localeCompare(b.date));
    return calculateAverages(results, sorted[0].date, sorted[sorted.length - 1].date);
  }, []);

  const calculatePeriodTotals = useCallback((results: DailyProductionResult[]): ProductionTotals => {
    if (results.length === 0) {
      const today = new Date().toISOString().split('T')[0];
      return calculateTotals([], today, today);
    }
    const sorted = [...results].sort((a, b) => a.date.localeCompare(b.date));
    return calculateTotals(results, sorted[0].date, sorted[sorted.length - 1].date);
  }, []);

  // Tank utilities
  const getTankFillPercent = useCallback((volumeBBL: number, capacityBBL: number): number => {
    return ProductionCalculatorService.getTankFillPercent(volumeBBL, capacityBBL);
  }, []);

  const getTankStatus = useCallback((fillPercent: number) => {
    return ProductionCalculatorService.getTankStatus(fillPercent);
  }, []);

  const getStandardTankConfig = useCallback((sizeBBL: number): TankConfiguration | undefined => {
    return STANDARD_TANK_CONFIGS[sizeBBL];
  }, []);

  // Quick calculations
  const calculateNetOil = useCallback((grossOil: number, kolorKutWater: number): number => {
    return Math.max(0, Math.round((grossOil - kolorKutWater) * 100) / 100);
  }, []);

  const calculateWaterCutPercent = useCallback((oil: number, water: number): number => {
    const total = oil + water;
    if (total <= 0) return 0;
    return Math.round((water / total) * 1000) / 10;
  }, []);

  const calculateGasOilRatio = useCallback((gasMCF: number, oilBBL: number): number => {
    if (oilBBL <= 0) return 0;
    return Math.round((gasMCF / oilBBL) * 100) / 100;
  }, []);

  const calculateDailyRevenue = useCallback((oilBBL: number, waterBBL: number) => {
    const oilRevenue = oilBBL * oilPricePerBBL;
    const waterCost = waterBBL * (waterHaulCostPerBBL + waterDisposalCostPerBBL);
    const netRevenue = oilRevenue - waterCost;
    
    return {
      oilRevenue: Math.round(oilRevenue * 100) / 100,
      waterCost: Math.round(waterCost * 100) / 100,
      netRevenue: Math.round(netRevenue * 100) / 100,
    };
  }, [oilPricePerBBL, waterHaulCostPerBBL, waterDisposalCostPerBBL]);

  // Reset state
  const reset = useCallback(() => {
    setState({
      result: null,
      comparison: null,
      isCalculating: false,
      error: null,
      lastCalculated: null,
    });
  }, []);

  return {
    // State
    ...state,
    
    // Main functions
    calculateProduction,
    calculateWithComparison,
    
    // Utility functions
    convertInchesToBarrels,
    getBblPerInchForTank,
    calculateKolorKut,
    
    // Period calculations
    calculatePeriodAverages,
    calculatePeriodTotals,
    
    // Tank utilities
    getTankFillPercent,
    getTankStatus,
    getStandardTankConfig,
    
    // Quick calculations
    calculateNetOil,
    calculateWaterCutPercent,
    calculateGasOilRatio,
    calculateDailyRevenue,
    
    // Reset
    reset,
  };
}

// ==================== ADDITIONAL HOOKS ====================

/**
 * Hook for quick tank calculations
 */
export function useTankCalculations() {
  return {
    getBblPerInch,
    inchesToBarrels,
    getTankFillPercent: ProductionCalculatorService.getTankFillPercent,
    getTankStatus: ProductionCalculatorService.getTankStatus,
    STANDARD_TANK_CONFIGS,
  };
}

/**
 * Hook for production metrics display
 */
export function useProductionMetrics(
  oilBBL: number,
  waterBBL: number,
  gasMCF: number,
  oilPricePerBBL: number = 75,
  waterCostPerBBL: number = 4.00
) {
  return useMemo(() => ({
    waterCutPercent: oilBBL + waterBBL > 0 
      ? Math.round((waterBBL / (oilBBL + waterBBL)) * 1000) / 10 
      : 0,
    gasOilRatio: oilBBL > 0 
      ? Math.round((gasMCF / oilBBL) * 100) / 100 
      : 0,
    totalFluidBBL: Math.round((oilBBL + waterBBL) * 100) / 100,
    oilRevenue: Math.round(oilBBL * oilPricePerBBL * 100) / 100,
    waterCost: Math.round(waterBBL * waterCostPerBBL * 100) / 100,
    netRevenue: Math.round((oilBBL * oilPricePerBBL - waterBBL * waterCostPerBBL) * 100) / 100,
    costPerBBLOil: oilBBL > 0 
      ? Math.round((waterBBL * waterCostPerBBL / oilBBL) * 100) / 100 
      : 0,
  }), [oilBBL, waterBBL, gasMCF, oilPricePerBBL, waterCostPerBBL]);
}

export default useProductionCalculator;
